import { Navigate, Outlet } from 'react-router'
import { useAuthStore } from '@/features/auth/store/store.ts'
import type { UserRoles } from '@/features/auth/types'
import { useLuxeFeatureFlag } from '@/features/luxe-submissions/queries/use-get-luxe-feature-flags'
import type { LuxeFeatureFlagKey } from '@/features/luxe-submissions/types'
import { routes } from '@/routes'

function RoleBasedRouteGuard({
	allowedRoles,
	flaggedRoles
}: {
	allowedRoles: UserRoles[]
	/**
	 * Roles that gain access only while the given Visual Vetting 2.0 flag is on.
	 * Until the flags have loaded the extra roles stay locked out, so the route
	 * behaves exactly as it does today rather than flashing into view.
	 */
	flaggedRoles?: { flag: LuxeFeatureFlagKey; roles: UserRoles[] }
}) {
	const user = useAuthStore((state) => state.user)
	const { isEnabled, isLoading } = useLuxeFeatureFlag(flaggedRoles?.flag)

	const rolesWithAccess =
		flaggedRoles && isEnabled
			? [...allowedRoles, ...flaggedRoles.roles]
			: allowedRoles

	const hasAccess = user?.role ? rolesWithAccess.includes(user.role) : false

	if (!hasAccess) {
		// A flagged role may still be waiting on the flag response — hold the
		// redirect until it arrives instead of bouncing them to Welcome.
		if (
			flaggedRoles &&
			isLoading &&
			user?.role &&
			flaggedRoles.roles.includes(user.role)
		) {
			return null
		}
		return <Navigate replace to={routes.WELCOME.path} />
	}

	return <Outlet />
}

export { RoleBasedRouteGuard }
